import Swal from 'sweetalert2';
import { escapeHtml } from '@/utils/escapeHtml';
import { Chat } from '@/mocks/app/mockChatData';
import { useBotsStore } from '@/stores/app/botStore';
import { ChatStore } from '@/stores/app/chatStore';
import { IdAtendimento, IdBot } from '@/services/common-types';
import { usePermissoesStore } from '@/stores/app/permissoesStore';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { toast } from 'vue3-toastify';
import { branding } from '@/config/branding';

const brandColor = branding.colors.light.primary;

export async function PromptToFinalizarAtendimento(chat: Chat, chatStore: ChatStore) {
    const permStore = usePermissoesStore();
    
    const authStore = useAuthStoreUser();
    const currentAccountId = authStore.currentConta?.id;
    if (!currentAccountId) {
        return toast.error("Não é possível executar essa ação sem nenhuma conta ativa.");
    }

    const permitirNaoAvaliar = permStore.temPermissao("conta.avaliacao_atendimento_facultativa", currentAccountId);
    const botStore = useBotsStore();

    const inboxComercial = !!(authStore.inboxes[chat.idInbox] as { comercial?: boolean } | undefined)?.comercial;

    const botsAvaliacaoDisponiveis = botStore.bots.filter(b =>
        !b.del &&
        b.tipo === 'AVALIACAO' &&
        b.inbox.id === chat.idInbox
    );

    if (botsAvaliacaoDisponiveis.length === 0) {
        const resultFinalizar = await Swal.fire({
            title: '',
            html: `
                <div class="d-flex flex-column align-items-center pt-2">
                    <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle"
                            style="width: 72px; height: 72px;">
                        <i class="ri-logout-box-r-line text-danger" style="font-size: 32px;"></i>
                    </div>
                    <h4 class="fw-bold mb-2 text-dark">Finalizar Atendimento?</h4>
                    <p class="text-muted px-3" style="margin-bottom: 1.25rem;">
                        O atendimento com <strong>${escapeHtml(chat.name)}</strong> será encerrado e arquivado.
                    </p>
                </div>
                ${inboxComercial ? `
                <div class="border-top pt-3 pb-1 px-4 text-start">
                    <div class="d-flex align-items-center justify-content-between">
                        <span class="fw-semibold text-dark" style="font-size: 0.9rem;">
                            <i class="ri-exchange-dollar-line me-1 text-success"></i>Houve conversão?
                        </span>
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" id="swal-conversao-switch"
                                style="width: 2.5em; height: 1.25em; cursor: pointer;">
                        </div>
                    </div>
                    <div id="swal-conversao-valor-wrapper" class="mt-2" style="display: none;">
                        <label class="form-label text-muted mb-1" style="font-size: 0.82rem;">Valor da conversão</label>
                        <div class="input-group input-group-sm">
                            <span class="input-group-text bg-light border-end-0 text-muted">R$</span>
                            <input type="text" id="swal-conversao-valor"
                                class="form-control border-start-0 rounded-end"
                                placeholder="0,00"
                                inputmode="decimal">
                        </div>
                    </div>
                </div>` : ''}
            `,
            showCancelButton: true,
            confirmButtonText: 'Sim, Finalizar',
            cancelButtonText: 'Cancelar',
            buttonsStyling: false,
            customClass: {
                popup: 'rounded-4 border-0 shadow-lg',
                confirmButton: 'btn btn-danger rounded-pill px-4 shadow-sm',
                cancelButton: 'btn btn-light rounded-pill px-4 border text-muted',
                actions: 'w-100 pb-4 px-2 d-flex justify-content-center gap-2'
            },
            didOpen: (popup) => {
                if (!inboxComercial) return;
                const toggle = popup.querySelector('#swal-conversao-switch') as HTMLInputElement | null;
                const wrapper = popup.querySelector('#swal-conversao-valor-wrapper') as HTMLElement | null;
                const valorInput = popup.querySelector('#swal-conversao-valor') as HTMLInputElement | null;
                if (!toggle || !wrapper || !valorInput) return;

                valorInput.addEventListener('input', () => {
                    const digits = valorInput.value.replace(/\D/g, '');
                    const cents = parseInt(digits || '0', 10);
                    valorInput.value = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                });

                toggle.addEventListener('change', () => {
                    wrapper.style.display = toggle.checked ? 'block' : 'none';
                    if (toggle.checked) valorInput.focus();
                    else valorInput.value = '';
                });
            },
            preConfirm: () => {
                if (!inboxComercial) return undefined;
                const toggle = document.getElementById('swal-conversao-switch') as HTMLInputElement | null;
                // Switch desligado é RESPOSTA ("não houve"), não silêncio: devolver
                // `undefined` aqui tornava a negativa indistinguível de quem nunca foi
                // perguntado (lote, transferir-e-finalizar, ramo Avaliar), e o servidor
                // não tinha como registrar a anotação de conversão sem chutar.
                if (!toggle?.checked) return { conversao: false, conversaoValor: 0 };
                const digits = (document.getElementById('swal-conversao-valor') as HTMLInputElement | null)?.value.replace(/\D/g, '') ?? '';
                return { conversao: true, conversaoValor: parseInt(digits || '0', 10) };
            }
        });

        if (resultFinalizar.isConfirmed) {
            await chatStore.finalizarAtendimentoAtivo(
                chat.id as IdAtendimento,
                resultFinalizar.value as { conversao?: boolean; conversaoValor?: number } | undefined
            );
        }
        return;
    }

    const resultAvaliacao = await Swal.fire({
        title: '', // Título vazio para usar o do HTML
        html: `
            <div class="d-flex flex-column align-items-center pt-2">
                <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle"
                    style="width: 72px; height: 72px; background-color: #fdf2f8;">
                    <i class="ri-star-smile-fill" style="font-size: 36px; color: ${brandColor};"></i>
                </div>
                <h4 class="fw-bold mb-2 text-dark">Pesquisa de Satisfação</h4>
                <p class="text-muted mb-0 px-2" style="font-size: 0.95rem; line-height: 1.5;">
                    Deseja enviar uma avaliação para <strong class="text-dark">${escapeHtml(chat.name)}</strong> antes de encerrar o atendimento?
                </p>
            </div>
        `,
        showCloseButton: true,
        showDenyButton: permitirNaoAvaliar,
        showCancelButton: true,

        confirmButtonText: '<i class="ri-send-plane-fill me-1"></i> Sim, Avaliar',
        denyButtonText: 'Apenas Finalizar',
        cancelButtonText: 'Cancelar',

        buttonsStyling: false,
        customClass: {
            popup: 'rounded-4 border-0 shadow-lg',
            confirmButton: 'btn btn-primary rounded-pill px-4 fw-semibold mx-1 shadow-sm',
            denyButton: 'btn btn-light rounded-pill px-4 fw-semibold mx-1 border text-muted',
            cancelButton: 'btn btn-link text-muted text-decoration-none mx-1',
            actions: 'w-100 pb-4 px-2 d-flex justify-content-center flex-wrap-reverse gap-2'
        },

        didOpen: (popup) => {
            const btnConfirm = popup.querySelector('.swal2-confirm') as HTMLElement;
            if(btnConfirm) {
                btnConfirm.style.backgroundColor = brandColor;
                btnConfirm.style.borderColor = brandColor;
            }
        }
    });

    if (resultAvaliacao.isConfirmed) {

        const botsDisponiveis = botsAvaliacaoDisponiveis;

        if (botsDisponiveis.length === 0) {
            void Swal.fire({
                html: `
                    <div class="d-flex flex-column align-items-center py-2">
                        <i class="ri-error-warning-line text-warning mb-3" style="font-size: 48px;"></i>
                        <h5 class="fw-bold text-dark">Nenhum Bot Encontrado</h5>
                        <p class="text-muted mb-0">Não há fluxos de automação configurados neste inbox.</p>
                    </div>
                `,
                confirmButtonText: 'Entendi',
                buttonsStyling: false,
                customClass: {
                    popup: 'rounded-4 border-0 shadow-lg',
                    confirmButton: 'btn btn-dark rounded-pill px-4'
                }
            });
            return;
        }

        let botIdSelecionado = botsDisponiveis[0].id;

        if (botsDisponiveis.length > 1) {
            const inputOptions: Record<string, string> = {};
            // Swal monta o rótulo das opções como HTML.
        botsDisponiveis.forEach(b => inputOptions[b.id] = escapeHtml(b.nome));

            const { value: botId }: {value?: IdBot} = await Swal.fire({
                title: '',
                html: `
                    <div class="d-flex flex-column align-items-center pt-2 mb-3">
                        <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light"
                            style="width: 60px; height: 60px;">
                            <i class="ri-flow-chart text-primary" style="font-size: 28px;"></i>
                        </div>
                        <h5 class="fw-bold text-dark">Selecione a Avaliação</h5>
                        <p class="text-muted mb-0 font-size-14">Qual fluxo deseja enviar?</p>
                    </div>
                `,
                input: 'select',
                inputOptions: inputOptions,
                inputPlaceholder: 'Selecione um fluxo...',
                showCancelButton: true,
                confirmButtonText: 'Enviar Bot',
                cancelButtonText: 'Cancelar',
                buttonsStyling: false,
                customClass: {
                    popup: 'rounded-4 border-0 shadow-lg',
                    confirmButton: 'btn btn-primary rounded-pill px-4 shadow-sm',
                    cancelButton: 'btn btn-light rounded-pill px-4 border',
                    input: 'form-select rounded-3 mt-2'
                },
                didOpen: (popup) => {
                    const btnConfirm = popup.querySelector('.swal2-confirm') as HTMLElement;
                    if(btnConfirm) {
                        btnConfirm.style.backgroundColor = brandColor;
                        btnConfirm.style.borderColor = brandColor;
                    }
                }
            });

            if (!botId) return;
            botIdSelecionado = botId;
        }

        void chatStore.dispararBot(chat.id as IdAtendimento, botIdSelecionado);

        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        })

        void Toast.fire({
            icon: 'success',
            title: 'Pesquisa de satisfação enviada!'
        })

    }
    // --- OPÇÃO 2: NÃO, APENAS FINALIZAR (Fluxo Padrão) ---
    else if (resultAvaliacao.isDenied) {

        const resultFinalizar = await Swal.fire({
            title: '',
            html: `
                <div class="d-flex flex-column align-items-center pt-2">
                    <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle"
                            style="width: 72px; height: 72px;">
                        <i class="ri-logout-box-r-line text-danger" style="font-size: 32px;"></i>
                    </div>
                    <h4 class="fw-bold mb-2 text-dark">Finalizar Atendimento?</h4>
                    <p class="text-muted px-3" style="margin-bottom: 1.25rem;">
                        O atendimento com <strong>${escapeHtml(chat.name)}</strong> será encerrado e arquivado.
                    </p>
                </div>
                ${inboxComercial ? `
                <div class="border-top pt-3 pb-1 px-4 text-start">
                    <div class="d-flex align-items-center justify-content-between">
                        <span class="fw-semibold text-dark" style="font-size: 0.9rem;">
                            <i class="ri-exchange-dollar-line me-1 text-success"></i>Houve conversão?
                        </span>
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" id="swal-conversao-switch"
                                style="width: 2.5em; height: 1.25em; cursor: pointer;">
                        </div>
                    </div>
                    <div id="swal-conversao-valor-wrapper" class="mt-2" style="display: none;">
                        <label class="form-label text-muted mb-1" style="font-size: 0.82rem;">Valor da conversão</label>
                        <div class="input-group input-group-sm">
                            <span class="input-group-text bg-light border-end-0 text-muted">R$</span>
                            <input type="text" id="swal-conversao-valor"
                                class="form-control border-start-0 rounded-end"
                                placeholder="0,00"
                                inputmode="decimal">
                        </div>
                    </div>
                </div>` : ''}
            `,
            showCancelButton: true,
            confirmButtonText: 'Sim, Finalizar',
            cancelButtonText: 'Cancelar',
            buttonsStyling: false,
            customClass: {
                popup: 'rounded-4 border-0 shadow-lg',
                confirmButton: 'btn btn-danger rounded-pill px-4 shadow-sm',
                cancelButton: 'btn btn-light rounded-pill px-4 border text-muted',
                actions: 'w-100 pb-4 px-2 d-flex justify-content-center gap-2'
            },
            didOpen: (popup) => {
                if (!inboxComercial) return;
                const toggle = popup.querySelector('#swal-conversao-switch') as HTMLInputElement | null;
                const wrapper = popup.querySelector('#swal-conversao-valor-wrapper') as HTMLElement | null;
                const valorInput = popup.querySelector('#swal-conversao-valor') as HTMLInputElement | null;
                if (!toggle || !wrapper || !valorInput) return;

                valorInput.addEventListener('input', () => {
                    const digits = valorInput.value.replace(/\D/g, '');
                    const cents = parseInt(digits || '0', 10);
                    valorInput.value = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                });

                toggle.addEventListener('change', () => {
                    wrapper.style.display = toggle.checked ? 'block' : 'none';
                    if (toggle.checked) valorInput.focus();
                    else valorInput.value = '';
                });
            },
            preConfirm: () => {
                if (!inboxComercial) return undefined;
                const toggle = document.getElementById('swal-conversao-switch') as HTMLInputElement | null;
                // Switch desligado é RESPOSTA ("não houve"), não silêncio: devolver
                // `undefined` aqui tornava a negativa indistinguível de quem nunca foi
                // perguntado (lote, transferir-e-finalizar, ramo Avaliar), e o servidor
                // não tinha como registrar a anotação de conversão sem chutar.
                if (!toggle?.checked) return { conversao: false, conversaoValor: 0 };
                const digits = (document.getElementById('swal-conversao-valor') as HTMLInputElement | null)?.value.replace(/\D/g, '') ?? '';
                return { conversao: true, conversaoValor: parseInt(digits || '0', 10) };
            }
        });

        if (resultFinalizar.isConfirmed) {
            await chatStore.finalizarAtendimentoAtivo(
                chat.id as IdAtendimento,
                resultFinalizar.value as { conversao?: boolean; conversaoValor?: number } | undefined
            );
        }
    }
}
